import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {ContentService} from "../_services/content.service";
import {CreatePostComponent} from "../shared/create-post/create-post.component";
import {User} from '../_types/user';
import {DashboardFeedComponent} from './dashboard-feed/dashboard-feed.component';
import {AuthService} from '../_services/auth.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Subscription, take} from 'rxjs';
import {FeedPayload, FeedPost} from '../_types/content';
import {CommentsComponent} from "../shared/comments/comments.component";
import {Community, UserAccessLevel} from "../_types/communities";
import {ActivatedRoute, Router} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ThemeService} from "../_services/theme.service";
import {CommunitiesService} from "../_services/communities.service";

@Component({
    selector: 'app-feed',
    templateUrl: './feed.component.html',
    styleUrl: './feed.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FeedComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(DashboardFeedComponent) dashboardFeedComponent!: DashboardFeedComponent;
    currentCommunityId: number | null = null;
    loading: boolean = false;
    sidebarPosts: FeedPost[] = [];
    canPostHere: boolean = false;
    onAdminGeneratedSubgroup: boolean = false;
    onMemberGeneratedSubgroup: boolean = false;
    community: Community | null = null
    isMobile: boolean = false;
    private querySub!: Subscription;
    private sidebarSub?: Subscription;

    constructor(public dialog: MatDialog,
                private authService: AuthService,
                private contentService: ContentService,
                private communitiesService: CommunitiesService,
                private router: Router,
                private route: ActivatedRoute,
                protected themeService: ThemeService,
                private readonly snackBar: MatSnackBar,
                public changeDetection: ChangeDetectorRef
    ) {

        this.route.queryParams.pipe(takeUntilDestroyed()).subscribe((params): void => {
                if (params['AICommentBuilderJob'] && params['post']) {
                    this.contentService.queryAIPollSummaryJob(params['AICommentBuilderJob']).pipe(take(1)).subscribe({
                        next: (AiCommentBuilderRes: any): void => {
                            this.contentService.getPostById(params['post']).pipe(take(1))
                                .subscribe({
                                    next: (post: any): void => {
                                        this.contentService.getPostComments(post.id).pipe(take(1)).subscribe({
                                            next: (comments: any): void => {
                                                this.changeDetection.detach();
                                                const dialogRef: MatDialogRef<CommentsComponent> = this.dialog.open(CommentsComponent, {
                                                    data: {
                                                        comments: comments,
                                                        post,
                                                        prefillComment: AiCommentBuilderRes.data.draft,
                                                    },
                                                    autoFocus: false,
                                                    panelClass: ['mediumWindow', 'w-100', 'comments-modal'],
                                                });
                                                dialogRef.afterClosed().subscribe((): void => {
                                                    this.changeDetection.reattach();
                                                });
                                            },
                                            error: err => {
                                                this.openSnackBar(err, 'error');
                                                this.router.navigate(['/feed'], {relativeTo: this.route});
                                            }
                                        })

                                        this.router.navigate(['/feed'], {relativeTo: this.route});
                                    },
                                    error: err => {
                                        this.openSnackBar(err, 'error');
                                        this.router.navigate(['/feed'], {relativeTo: this.route});
                                    }
                                })
                        },
                        error: err => {
                            this.loading = false;
                            this.openSnackBar(err, 'error');
                        }
                    })

                } else if (params['Survey'] || params['survey']) {
                    let surveyID: number = JSON.parse(params['survey']) || JSON.parse(params['Survey'])
                    if (surveyID) {
                        if (params['id'] && this.currentUser && (params['id'] != this.currentUser.id)) {
                            this.openSnackBar('Survey was completed by a different user', 'error')
                            return
                        }
                        this.contentService.postSurveyComplete(surveyID).subscribe((r: any): void => {
                            this.router.navigate(['/feed'], {relativeTo: this.route}).then(function (): void {
                                location.reload();
                            })
                        }, error => {
                            this.router.navigate(['/feed'], {relativeTo: this.route}).then((): void => {
                                this.openSnackBar(error, 'error');
                            });
                        })
                    }
                }
            }
        );

        this.route.params
            .pipe(takeUntilDestroyed())
            .subscribe(params => {
                if (params['post']) {
                    this.contentService.getPostById(params['post']).pipe(take(1))
                        .subscribe({
                            next: (post: any): void => {
                                this.contentService.getPostComments(post.id).pipe(take(1)).subscribe({
                                    next: (comments: any): void => {
                                        this.changeDetection.detach();
                                        const dialogRef: MatDialogRef<CommentsComponent> = this.dialog.open(CommentsComponent, {
                                            data: {
                                                comments: comments,
                                                post,
                                            },
                                            autoFocus: false,
                                            panelClass: ['mediumWindow', 'w-100', 'comments-modal'],
                                        });
                                        dialogRef.afterClosed().subscribe((): void => {
                                            this.changeDetection.reattach();
                                        });
                                    },
                                    error: err => {
                                        this.openSnackBar(err, 'error');
                                        this.router.navigate(['/feed'], {relativeTo: this.route});
                                    }
                                })
                                this.router.navigate(['/feed'], {
                                    relativeTo: this.route,
                                    queryParamsHandling: 'preserve'
                                });
                            },
                            error: err => {
                                this.openSnackBar(err, 'error');
                                this.router.navigate(['/feed'], {relativeTo: this.route});
                            }
                        })
                }
            });

        this.communitiesService.getCurrentDashboardCommunity()
            .pipe(takeUntilDestroyed())
            .subscribe(community => {
                this.community = community;
                this.currentCommunityId = community?.id ?? null;
                this.loadPostFromSidebar(this.currentCommunityId);
                // Community flags can arrive after the route parameters.
                this.checkIfUserCanPost();
            });
    }

    get isSuperuser(): boolean {
        return this.authService.isSuperUser();
    }

    get currentUser(): User {
        return this.authService.getUser();
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any): void {
        this.isMobile = window.innerWidth <= 1000;
    }

    ngOnInit(): void {
        this.isMobile = window.innerWidth <= 1000;

        this.querySub = this.route.queryParamMap.subscribe(params => {
            this.checkIfUserCanPost();
            this.loadPostFromSidebar(this.currentCommunityId);
        });
    }

    ngAfterViewInit(): void {
        this.checkIfUserCanPost();
    }

    ngOnDestroy(): void {
        this.sidebarSub?.unsubscribe();
        // Always clean up subscriptions to prevent memory leaks
        if (this.querySub) {
            this.querySub.unsubscribe();
        }
    }

    checkIfUserCanPost(): void {
        let subgroup: string | null = this.route.snapshot.queryParamMap.get('subgroup')
        this.onAdminGeneratedSubgroup = false;
        this.onMemberGeneratedSubgroup = false;
        if ((this.community?.show_start_a_post_on_admin_generated_subgroup || this.community?.show_start_a_post_on_member_generated_subgroup) && subgroup) {
            // The community shows 'Start a post' on the admin generated subgroup
            // to admins only and/or on the member generated subgroup to members
            // only; check whether this is one of those subgroups
            this.communitiesService.getSubGroup(this.community.id, JSON.parse(subgroup)).subscribe({
                next: result => {
                    this.onAdminGeneratedSubgroup = !!this.community?.show_start_a_post_on_admin_generated_subgroup && !!result.data.admin_generated_subgroup;
                    this.onMemberGeneratedSubgroup = !!this.community?.show_start_a_post_on_member_generated_subgroup && !!result.data.member_generated_subgroup;
                }, error: err => {
                    console.error(err);
                }
            })
        }
        if (this.community?.members_can_only_post_in_member_subgroup && this.hasCommunityRole(['member'])) {
            // Member only subgroup posting
            if (subgroup) {
                //  check they can post in this subgroup
                this.communitiesService.getSubGroup(this.community.id, JSON.parse(subgroup)).subscribe({
                    next: result => {
                        this.canPostHere = !!result.data.member_generated_subgroup
                    }, error: err => {
                        console.error(err);
                        this.canPostHere = false;
                    }
                })
            } else {
                // cant post in main feed
                this.canPostHere = false;
            }
        } else if (this.community?.admins_can_only_post_in_admin_subgroup && (this.hasCommunityRole(['full_admin']) || this.isSuperuser)) {
            // Admin only subgroup posting
            if (subgroup) {
                //  check they can post in this subgroup
                this.communitiesService.getSubGroup(this.community.id, JSON.parse(subgroup)).subscribe({
                    next: result => {
                        this.canPostHere = !!result.data.admin_generated_subgroup
                    }, error: err => {
                        console.error(err);
                        this.canPostHere = false;
                    }
                })
            } else {
                // cant post in main feed
                this.canPostHere = false;
            }
        } else {
            this.canPostHere = true;
        }
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.communitiesService.userHasCommunityRole(roles);
    }

    get showStartAPostButton(): boolean {
        // if (this.onAdminGeneratedSubgroup) {
        //     // admins only on the admin generated subgroup
        //     return this.isSuperuser || this.hasCommunityRole(['full_admin']);
        // }
        if ((this.isSuperuser || this.hasCommunityRole(['full_admin']))) {
            return true;
        }
        if (this.onMemberGeneratedSubgroup) {
            // members only on the member generated subgroup
            return !!this.community?.allow_members_to_post && this.hasCommunityRole(['member']);
        }
        return (this.isSuperuser || this.hasCommunityRole(['full_admin', 'moderator', 'client'])
            || (!!this.community?.allow_members_to_post && this.hasCommunityRole(['member']))) && this.canPostHere;
    }

    newPost(): void {
        this.changeDetection.detach();
        const subgroup: string | null = this.route.snapshot.queryParamMap.get('subgroup');
        const dialogRef: MatDialogRef<CreatePostComponent> = this.dialog.open(CreatePostComponent, {
            panelClass: ['largeWindow', 'w-100'],
            data: {
                subgroup
            }
        });
        dialogRef.afterClosed().pipe(take(1)).subscribe((result: boolean): void => {
            this.changeDetection.reattach();
            if (result) {
                this.dashboardFeedComponent.reloadContent();
            }
        });
        dialogRef.disableClose = true;
    }


    loadPostFromSidebar(id: number | null): void {
        this.sidebarSub?.unsubscribe();
        this.sidebarPosts = [];
        this.loading = false;
        if (!id) return;
        const filters: FeedPayload = {is_pinned_to_sidebar: true, sort_by: 'newest'};
        const subgroup = Number(this.route.snapshot.queryParamMap.get('subgroup'));
        if (this.community?.separate_pinned_posts_by_subgroup && Number.isInteger(subgroup) && subgroup > 0) {
            filters.subgroup = subgroup;
        }
        this.loading = true;
        this.sidebarSub = this.contentService.getFeed(id, 1, 1000, filters)
            .pipe(take(1))
            .subscribe({
                next: result => {
                    this.sidebarPosts = result.results;
                    this.loading = false;
                }, error: result => {
                    this.loading = false;
                    this.openSnackBar(result, 'error');
                },
            });
    }


    trackByPostId(index: number, post: any): number {
        return post.id;
    }

    openSnackBar(message: string, type: string): void {
        this.snackBar.open(message, 'Dismiss', {
            duration: 5000,
            panelClass: [type]
        });
    }
}
